/** Normalize populated or raw ObjectId refs for comparison */
export const refId = (v) => {
  if (v == null || v === "") return null
  if (typeof v === "object" && v._id != null) return String(v._id)
  return String(v)
}

/** Client menu: show dish unless explicitly disabled (API often omits isActive) */
export const isMenuItemShown = (item) => item?.isActive !== false

/** Prix affichable côté client (valeur brute) */
export const hasMenuItemPrice = (price) => {
  if (price == null || price === "") return false
  return String(price).trim() !== ""
}

/**
 * Prix à afficher pour un plat : `price` / `prix`, puis traductions par langue.
 * Retourne une chaîne non vide ou `null`.
 */
export const getMenuItemDisplayPrice = (item, lang = "fr") => {
  if (item == null) return null
  const tr = item.translations || {}
  const candidates = [
    item.price,
    item.prix,
    tr[lang]?.price,
    tr.fr?.price,
    tr.en?.price,
    tr.ar?.price,
  ]
  for (const p of candidates) {
    if (p == null || p === "") continue
    const s = String(p).trim()
    if (s !== "") return s
  }
  return null
}

/**
 * Prix à afficher pour un lieu (petit-déj, en-cas, restaurant) : `price` / `prix`, puis traductions.
 */
export const getVenueDisplayPrice = (venue, lang = "fr") => {
  if (venue == null) return null
  const tr = venue.translations || {}
  const candidates = [
    venue.price,
    venue.prix,
    tr[lang]?.price,
    tr.fr?.price,
    tr.en?.price,
    tr.ar?.price,
  ]
  for (const p of candidates) {
    if (p == null || p === "") continue
    const s = String(p).trim()
    if (s !== "") return s
  }
  return null
}

/**
 * Tableau de plats : accepte array, chaîne JSON, ou objet indexé (0,1,2…).
 */
export const normalizeMenuItems = (items) => {
  if (items == null) return []
  if (Array.isArray(items)) return items
  if (typeof items === "string") {
    try {
      return normalizeMenuItems(JSON.parse(items))
    } catch {
      return []
    }
  }
  if (typeof items === "object") {
    const keys = Object.keys(items)
    if (keys.length > 0 && keys.every((k) => /^\d+$/.test(k))) {
      return keys
        .sort((a, b) => Number(a) - Number(b))
        .map((k) => items[k])
        .filter((x) => x != null)
    }
  }
  return []
}

/**
 * Extrait les plats d’un menu API (plusieurs noms de champs possibles).
 * Accepte aussi directement un tableau déjà prêt.
 */
export const getMenuItemsRaw = (menu) => {
  if (menu == null) return []
  if (Array.isArray(menu)) return normalizeMenuItems(menu)
  if (typeof menu !== "object") return normalizeMenuItems(menu)
  const fieldNames = ["items", "dishes", "lines", "plats", "products", "articles", "menuItems"]
  for (const k of fieldNames) {
    const n = normalizeMenuItems(menu[k])
    if (n.length > 0) return n
  }
  return []
}

/**
 * Plats pour l’affichage client : filtre isActive, avec repli si tout serait masqué.
 */
export const filterItemsForVenueClient = (menuOrItems) => {
  const arr = getMenuItemsRaw(menuOrItems)
  if (arr.length === 0) return []
  const filtered = arr.filter((item) => isMenuItemShown(item))
  if (filtered.length === 0) {
    return arr
  }
  return filtered
}

export const menusArrayFromResponse = (data) => {
  if (Array.isArray(data)) return data
  if (data && Array.isArray(data.menus)) return data.menus
  if (data && Array.isArray(data.data)) return data.data
  return []
}

const REF_SCAN_EXCLUDE = new Set([
  "_id",
  "__v",
  "title",
  "items",
  "translations",
  "images",
  "createdAt",
  "updatedAt",
  "order",
  "sort",
  "name",
  "description",
  "type",
  "slug",
  "price",
  "isActive",
  "isVegetarian",
  "isOrganic",
  "isLocal",
  "allergenArachide",
  "allergenCeleri",
  "allergenCrustaces",
  "allergenGluten",
  "allergenFruitsANoque",
  "allergenLait",
  "allergenLupin",
  "allergenOeuf",
  "allergenPoisson",
  "allergenMollusques",
  "allergenMoutarde",
  "allergenSesame",
  "allergenSoja",
  "allergenSulfites",
  "commandable",
  "isAvailable24_7",
])

const PETIT_DEJEUNER_REF_KEYS = [
  "petitDejeuner",
  "petit_dejeuner",
  "petitdejeuner",
  "PetitDejeuner",
  "petitDejeunerId",
  "petit_dejeuner_id",
  "petit_dejeuners",
  "petitDejeuners",
  "restaurant",
]

const valueMatchesId = (v, rid) => {
  if (v == null) return false
  if (Array.isArray(v)) return v.some((x) => refId(x) === rid)
  return refId(v) === rid
}

export const menuMatchesRestaurant = (menu, venueId) => {
  const rid = String(venueId)
  return refId(menu.restaurant) === rid
}

const ENCAS_REF_KEYS = [
  "enCas",
  "encas",
  "EnCas",
  "en_cas",
  "restaurant",
]

/** Comme le petit-déj : champs explicites + scan des refs (API peut nommer le lien autrement) */
export const menuMatchesEnCas = (menu, venueId) => {
  const rid = String(venueId)
  for (const k of ENCAS_REF_KEYS) {
    if (valueMatchesId(menu[k], rid)) return true
  }
  for (const [k, v] of Object.entries(menu)) {
    if (REF_SCAN_EXCLUDE.has(k)) continue
    if (valueMatchesId(v, rid)) return true
  }
  return false
}

export const menuMatchesPetitDejeuner = (menu, venueId) => {
  const rid = String(venueId)
  for (const k of PETIT_DEJEUNER_REF_KEYS) {
    if (valueMatchesId(menu[k], rid)) return true
  }
  for (const [k, v] of Object.entries(menu)) {
    if (REF_SCAN_EXCLUDE.has(k)) continue
    if (valueMatchesId(v, rid)) return true
  }
  return false
}
